import type { Browser } from "puppeteer-core";

import { launchBrowser, type LaunchedBrowser } from "./launchBrowser";

type BrowserRequest = {
  executablePath?: string;
};

class BrowserManager {
  private browser: Browser | undefined;
  private requestKey: string | undefined;
  private launchPromise: Promise<LaunchedBrowser> | undefined;
  private launchRequestKey: string | undefined;

  async getBrowser(request: BrowserRequest = {}): Promise<Browser> {
    const nextRequestKey = request.executablePath ?? "auto";

    if (this.browser?.isConnected() && this.requestKey === nextRequestKey) {
      return this.browser;
    }

    if (this.launchPromise && this.launchRequestKey === nextRequestKey) {
      const launched = await this.launchPromise;
      return launched.browser;
    }

    if (this.browser?.isConnected() && this.requestKey !== nextRequestKey) {
      await this.close();
    }

    this.launchRequestKey = nextRequestKey;
    this.launchPromise = launchBrowser(request)
      .then((launched) => {
        this.browser = launched.browser;
        this.requestKey = nextRequestKey;

        this.browser.once("disconnected", () => {
          if (this.browser === launched.browser) {
            this.browser = undefined;
            this.requestKey = undefined;
          }
        });

        return launched;
      })
      .finally(() => {
        this.launchPromise = undefined;
        this.launchRequestKey = undefined;
      });

    const launched = await this.launchPromise;
    return launched.browser;
  }

  async close(): Promise<void> {
    const browser = this.browser;
    this.browser = undefined;
    this.requestKey = undefined;

    if (browser?.isConnected()) {
      await browser.close();
    }
  }
}

export const browserManager = new BrowserManager();

export async function closePdfEngine(): Promise<void> {
  await browserManager.close();
}
